import { Box, CircularProgress, Fade, AppBar, Toolbar, Container } from '@mui/material';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SideNav from './SideNav';
import Breadcrumbs from './Breadcrumbs';
import PilotModeBanner from './PilotModeBanner';
import ModeSwitchButton from '../shared/ModeSwitchButton';
import ContextIndicator from '../shared/ContextIndicator';
import ClinicSelector from '../shared/ClinicSelector';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';

const AppLayout = ({ children, navItems }) => {
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);
  const [showContent, setShowContent] = useState(true);

  // Enable keyboard shortcuts
  useKeyboardShortcuts({ enabled: true });

  const handleSidebarToggle = () => {
    // Sidebar state is self-managed; main content stays fixed
  };

  // Handle route changes with loading state
  useEffect(() => {
    setIsNavigating(true);
    setShowContent(false);

    // Short delay for smooth transition
    const timer = setTimeout(() => {
      setIsNavigating(false);
      setShowContent(true);
    }, 150);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Track last clinical route for "Back to Clinic" functionality
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Track clinical routes (don't track admin routes)
    if (currentPath.startsWith('/clinic-dashboard') || 
        currentPath.startsWith('/clinic-staff-dashboard') ||
        currentPath.startsWith('/physician') ||
        currentPath.startsWith('/dashboard') ||
        currentPath.startsWith('/sponsor-dashboard')) {
      localStorage.setItem('lastClinicalRoute', currentPath);
    }
  }, [location.pathname]);
  
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8f9fa', margin: 0, padding: 0, boxSizing: 'border-box' }}>
      {/* Always visible sidebar */}
      <SideNav items={navItems} onToggleExpand={handleSidebarToggle} />
      
      {/* Top Header Bar */}
      <AppBar 
        position="fixed"
        sx={{
          top: 0,
          left: '60px',
          right: 0,
          width: 'calc(100% - 60px)',
          bgcolor: 'primary.dark',
          color: 'white',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar disableGutters sx={{ px: 2, minHeight: '48px !important', height: '48px' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <ClinicSelector />
            <ContextIndicator />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ModeSwitchButton />
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Main content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1,
          p: 3,
          width: 'calc(100% - 60px)',
          ml: '60px', // Always keep the margin at the collapsed sidebar width
          mt: '48px', // Account for fixed header
          transition: 'all 0.3s ease',
          overflow: 'auto',
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        {/* Breadcrumbs */}
        <Breadcrumbs navItems={navItems} />

        {/* Pilot Mode Banner */}
        <PilotModeBanner />

        {/* Loading indicator during navigation */}
        {isNavigating && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}

        {/* Page content with fade transition */}
        <Fade in={showContent} timeout={200}>
          <Box sx={{ opacity: isNavigating ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>
            {children}
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default AppLayout;
